/* eslint-disable no-restricted-globals */
import { useMemo, useCallback } from 'react';

import useForm from '../utils/useForm';
import ServicerService from '../../services/ServicerService';
import createServicerBankAccountsOptions from '../../helpers/forms/createServicerBankAccountsOptions';
import VendorService from '../../services/VendorService';

const usePaymentsForm = (
  onSubmit,
  values,
  bankAccountId,
  bankAccounts,
  loanId
) => {
  const fields = useMemo(
    () => [
      {
        title: 'Create Payment',
        fields: [
          {
            title: 'Payer',
            name: 'payer',
            type: 'select',
            options: [
              { value: 'Borrower', label: 'Borrower' },
              { value: 'Lender', label: 'Lender' },
              { value: 'Servicer', label: 'Servicer' },
              { value: 'Vendor', label: 'Vendor' },
            ],
            required: true,
          },
          {
            title: 'Vendor',
            name: 'vendor_id',
            type: 'fetchableSelect',
            fetchOptions: {
              key: ['paymentsVendor', 'Insurance', loanId],
              queryFn: () => VendorService.getVendors({ loanId }),
              labelKey: 'name',
            },
            isHidden: (vals) => {
              return vals.payer !== 'Vendor';
            },
          },
          {
            title: 'Source',
            name: 'source',
            type: 'select',
            options: (vals) =>
              vals.payer === 'Vendor'
                ? [
                    { value: 'Check', label: 'Check' },
                    { value: 'ACH direct', label: 'ACH direct' },
                  ]
                : [
                    { value: 'ACH', label: 'ACH' },
                    { value: 'Check', label: 'Check' },
                    { value: 'ACH direct', label: 'ACH direct' },
                    { value: 'Outside', label: 'Outside' },
                    { value: 'Import', label: 'Import' },
                  ],
          },
          {
            title: 'Bank Account',
            name: 'bank_account_id',
            type: 'fetchableSelect',
            fetchOptions: {
              key: ['servicerBankAccountsOptions'],
              queryFn: async () => {
                try {
                  const servicerBankAccounts =
                    await ServicerService.getServicerBankAccounts();

                  const bankAccountsWithOptions =
                    createServicerBankAccountsOptions(servicerBankAccounts);

                  return bankAccountsWithOptions;
                } catch (error) {
                  console.log('Error fetching servicer bank accounts:', error);
                  return [];
                }
              },
              labelKey: 'option',
            },
            isHidden: (vals) => {
              return vals.source !== 'ACH' || vals.payer !== 'Servicer';
            },
          },
          {
            title: 'Bank Account',
            name: 'bank_account_id',
            type: 'select',
            options: bankAccounts?.map((account) => {
              return {
                value: account.id,
                label: `${account?.account_number?.replace(
                  /.(?=.{4})/g,
                  '* '
                )} ${account.id === bankAccountId ? ' (primary)' : ''}`,
              };
            }),
            defaultValue: bankAccountId,
            isHidden: (vals) => {
              return (
                vals?.payer !== 'Borrower' ||
                !bankAccounts ||
                bankAccounts.length < 2 ||
                vals.source !== 'ACH' ||
                (vals.source === 'ACH' && vals.payer === 'Servicer')
              );
            },
          },
          {
            title: 'Reference',
            name: 'reference',
            type: 'text',
          },
          {
            title: 'Amount',
            name: 'amount',
            type: 'currency',
            required: true,
          },
          {
            title: 'Date',
            name: 'date',
            type: 'date',
            required: true,
          },
          {
            title: 'Payoff?',
            name: 'is_payoff',
            type: 'checkbox',
            readOnly:
              values?.status === 'Applied' || values?.status === 'Partial',
            isHidden: (vals) => {
              return vals?.payer === 'Vendor';
            },
          },
        ],
      },
    ],
    [loanId, bankAccounts, bankAccountId, values?.status]
  );

  const limitVendorValues = useCallback((newValues, setFieldValue) => {
    if (newValues.payer !== 'Vendor') {
      setFieldValue('vendor_id', '');
      setFieldValue('is_payoff', '');
    }

    if (
      newValues.payer === 'Vendor' &&
      !['Check', 'ACH direct'].includes(newValues.source)
    ) {
      setFieldValue('source', '');
    }
  }, []);

  return useForm({
    fields,
    onSubmit,
    values,
    onValuesChange: limitVendorValues,
  });
};

export default usePaymentsForm;
